import { every, filter, isEmpty, maxBy, uniqBy } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { useTranslation } from 'react-i18next';
import { FormikValues } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import { Tooltip } from 'react-tooltip';

import { notify } from 'shared/components/notification/notification';
import { API_CONFIG, STROKESCOLORS, chartColors, colorPallet } from 'shared/constants/constants';
import { useQueryParams } from 'shared/hooks/useQueryParams';
import { State } from 'shared/interface';
import {
	cleanForecastItemName,
	hexToRgba,
	replaceHashtagFromUrl,
	getDataTypeKey,
	getDataTypeDisplayName,
	getColorForDataType
} from 'shared/util/utility';
import HttpService from 'shared/services/http.service';
import { createAction, defineChartMargins } from 'shared/util/utility';
import * as actionTypes from 'store/action-types';
import EmptyDataContainer from 'shared/components/emptyDataContainer/emptyDataContainer';
import { Loader } from 'shared/components/spinner/spinner';
import { ChartAxisPosition, ForeCastType, ScaleType } from 'shared/interface/enums';
import localizationConstants from 'shared/util/translation/constants';
import PermissionManager from 'shared/hoc/permission/permissionManager';
import { adminPermissionMapping } from 'shared/constants/permissionConstant';

import MaterialTables from '../component/materialTables';
import AdminExportButtons from '../component/adminExportButtons';
import ChartAdditionalInfo from '../component/chartAdditionalInfo';
import ChartConfigurationDropdowns from '../component/chartConfigurationDropdowns';
import ChartHeader from '../component/chartHeader';
import ChartSettingsPopup from '../component/chartSettingsPopup';
import ExportMaterialDataButtons from '../component/exportmaterialDataButtons';
import StockChart from '../component/stockChart';
import { useAdvanceChartConfig } from '../hooks/useAdvanceChartConfig';
import {
	IAdvanceChartConfigType,
	IChartData,
	IChartDetails,
	IChartItem,
	IChartPayloadType,
	IChartSettingsType,
	ICompareChartIndicators,
	ISearchMaterialData
} from '../interface/futureTable';
import { Translate } from 'shared/util/translation/translate';
import { ForeCastUnlockIcon } from 'shared/icons/icon';
import { barChartFrequencyMapper, getHighestPriorityFrequency } from '../util/chartUtility';
import ToggleSwitch from 'shared/components/switch/toggleSwitch';
import httpService from 'shared/services/http.service';

export type IComparativeChartArrayType = ISearchMaterialData[] | ICompareChartIndicators[];

interface IProps {
	compareValue: string;
}

export interface ITempObjProps {
	symbol: string;
	boerse: string;
	waehrung: string;
	unit: string;
	year: number | string;
}

const ChartScreenContainer: React.FC<IProps> = ({ compareValue }) => {
	const { t } = useTranslation();

	const { timeSpan, currency, elementId, isRedirectChartScreen } = useQueryParams();
	const { language, chartConfig } = useSelector((state: State) => {
		return { ...state.siteConfig, ...state.chartConfigReducer, ...state.dashboardChartReducer };
	});
	const [loading, setLoading] = useState(false);
	const [chartData, setChartData] = useState([] as IChartData[]);
	const [forCastedChartData, setForecastedChartData] = useState([] as IChartData[]); //initial data

	const [futureTableLoading, setFutureTableLoading] = useState(false);
	const [futureTableData, setFutureTableData] = useState([] as any);
	const [strokeColors] = useState(STROKESCOLORS);
	const [chartColor, setChartColor] = useState('');
	const [action, setAction] = useState('');
	const location = useLocation();
	const navigate = useNavigate();
	const [updateChartViewLoading, setUpdateChartViewLoading] = useState(false);
	const [isTableOpen, setIsTableOpen] = useState(false);
	const [allChartDetails, setAllChartDetails] = useState({} as { [key: string]: IChartDetails });
	const [updatedAllChartDetails, setUpdatedAllChartDetails] = useState({} as { [key: string]: IChartDetails });
	const [priceListLoading, setPriceListLoading] = useState(false);
	const [isIntraday, setIsIntraday] = useState(false);
	const [isVisibleForecastNotation, setIsVisibleForecastNotation] = useState<boolean>(false);
	const [isVisiblePdfModuleNotation, setIsVisiblePdfModuleNotation] = useState<boolean>(false);
	const [rearrangedData, setRearrangedData] = useState<any>([]);
	const [updatedChartSettings, setUpdatedChartSettings] = useState<IChartSettingsType[]>([]);
	const [barWidth, setBarWidth] = useState<{ [key: string]: number }>({});
	const [forecastKeys, setForecastKeys] = useState<string[]>([]);
	const [activeChartKey, setActiveChartKey] = useState<string>('');
	const [chartFrequency, setChartFrequency] = useState<string[]>([]);
	const [prevForecastKeys, setPrevForecastKeys] = useState<string[]>([]);
	const dispatch = useDispatch();
	const chartDetailKey = Object.keys(allChartDetails)[0];
	const forecastAccessValue = allChartDetails[chartDetailKey]?.forecastAccess;
	const highestPriorityFrequency = chartFrequency ? getHighestPriorityFrequency(chartFrequency) : null;
	const { isRedirectFromGlobalSearch } = useSelector((state: State) => state.chartConfigReducer);
	const [isToggleForecast, setIsToggleForecast] = useState(false);
	const [isPreviousToggleForecast, setIsPreviousToggleForecast] = useState(false);
	// Data points with state management per material
	const [dataPoints, setDataPoints] = useState<{ [materialKey: string]: Array<{ id: string; label: string; checked: boolean }> }>({});
	// User-selected per-material frequency mapping
	const [selectedFrequencies, setSelectedFrequencies] = useState<{ [materialKey: string]: string }>({});
	// State to track when we need to trigger comparison API calls (separate from isChartSettingUpdate)
	const [shouldTriggerComparison, setShouldTriggerComparison] = useState<boolean>(false);
	// Guard to ensure bar defaulting logic runs only once on initial render
	const hasInitializedBarDefaults = useRef(false);
	const [isExportDisabled, setIsExportDisabled] = useState(false);

	const {
		chartSettings,
		setChartSettings,
		scaleType,
		setScaleType,
		chartType,
		setChartType,
		spanObj,
		setSpanObj,
		basis_symbol,
		boerse,
		symbol,
		currency: defaultCurrency,
		setIsChartSettingUpdate,
		isChartSettingUpdate,
		setForecastSources,
		setMaterialSymbol,
		setIsFutureTable,
		currencyType,
		setCurrencyType
	} = useAdvanceChartConfig();
	// Capture the initial chart type to ensure we only run when default is bar
	const initialChartTypeRef = useRef(chartType);

	// Wrapper: keep dataPoints in sync with chartSettings (remove deleted materials, remap indexes)
	const applySetChartSettings = useCallback(
		(updaterOrValue: IChartSettingsType[] | ((prev: IChartSettingsType[]) => IChartSettingsType[])) => {
			const remapDataPoints = (
				prevPoints: { [materialKey: string]: Array<{ id: string; label: string; checked: boolean }> },
				nextSettings: IChartSettingsType[]
			) => {
				// Group existing selections by base key (symbol-boerse) preserving occurrence order
				const baseKeyToPointsArray: {
					[baseKey: string]: Array<Array<{ id: string; label: string; checked: boolean }>>;
				} = {};
				Object.entries(prevPoints).forEach(([key, value]) => {
					const match = key.match(/^(.*)-(\d+)$/);
					const baseKey = match ? match[1] : key.replace(/-\d+$/, '');
					if (!baseKeyToPointsArray[baseKey]) baseKeyToPointsArray[baseKey] = [];
					baseKeyToPointsArray[baseKey].push(value);
				});

				// Build new mapping; for duplicate materials, use occurrence index, do not clone first selection to all
				const occurrenceCounter: { [baseKey: string]: number } = {};
				const nextPoints: { [materialKey: string]: Array<{ id: string; label: string; checked: boolean }> } = {};
				nextSettings.forEach((setting, index) => {
					const baseKey = `${setting.symbol}-${setting.boerse}`;
					const occurrenceIndex = occurrenceCounter[baseKey] ?? 0;
					occurrenceCounter[baseKey] = occurrenceIndex + 1;

					const candidates = baseKeyToPointsArray[baseKey] || [];
					const ptsForThisOccurrence = candidates[occurrenceIndex];

					// Only preserve existing selections if they exist for this specific occurrence
					// Do NOT clone from first occurrence to new materials added for comparison
					if (ptsForThisOccurrence) {
						nextPoints[`${baseKey}-${index}`] = ptsForThisOccurrence;
					}
					// if still nothing, defaults will initialize elsewhere (via getDataPointsForMaterial)
				});
				return nextPoints;
			};

			if (typeof updaterOrValue === 'function') {
				setChartSettings((prev) => {
					const next = (updaterOrValue as (p: IChartSettingsType[]) => IChartSettingsType[])(prev);
					setDataPoints((prevPts) => remapDataPoints(prevPts, next));
					return next;
				});
			} else {
				const next = updaterOrValue as IChartSettingsType[];
				setChartSettings(next);
				setDataPoints((prevPts) => remapDataPoints(prevPts, next));
			}
		},
		[setChartSettings]
	);

	const handleCheckboxChange = useCallback(
		(materialKey: string, id: string) => {
			// Pre-validate without mutating state to avoid accidental API triggers
			const materialDataPoints = dataPoints[materialKey] || [];
			const isCurrentlyChecked = materialDataPoints.find((point) => point.id === id)?.checked;
			const totalCheckedCurrent = Object.values(dataPoints)
				.flat()
				.filter((point) => point && point.checked).length;
			const materialCheckedCountCurrent = materialDataPoints.filter((point) => point.checked).length;
			const prospectiveTotal = isCurrentlyChecked ? totalCheckedCurrent - 1 : totalCheckedCurrent + 1;
			const prospectiveMaterialCount = isCurrentlyChecked ? materialCheckedCountCurrent - 1 : materialCheckedCountCurrent + 1;

			// Validation rules (early return if invalid)
			if (chartType === 'bar') {
				if (!isCurrentlyChecked && prospectiveTotal > 3) {
					notify(t(localizationConstants.barChartNumberValidationMessage), 'error');
					return;
				}
			} else {
				if (!isCurrentlyChecked && prospectiveTotal > 9) {
					notify(t(localizationConstants.errorMessageForChartTypeSelection), 'error');
					return;
				}
			}
			// Prevent unchecking the last item for this material
			if (isCurrentlyChecked && prospectiveMaterialCount < 1) {
				notify(t(localizationConstants.errorMessageForChartCheckbox), 'error');
				return;
			}

			// Safe to update state now
			setDataPoints((prevPoints) => {
				const prevMaterialPoints = prevPoints[materialKey] || [];
				const updatedMaterialDataPoints = prevMaterialPoints.map((point) =>
					point.id === id ? { ...point, checked: !point.checked } : point
				);
				return {
					...prevPoints,
					[materialKey]: updatedMaterialDataPoints
				};
			});

			// Update fields on the existing chart setting instead of pushing a new one
			const parts = materialKey.split('-');
			const materialIndexStr = parts[parts.length - 1];
			const materialIndex = parseInt(materialIndexStr, 10);
			if (!Number.isNaN(materialIndex)) {
				applySetChartSettings((prev) => {
					const updated = prev.map((setting, idx) => {
						if (idx !== materialIndex) return setting;
						// Build fields array from currently checked data points for this material
						const currentPoints = (dataPoints[materialKey] || []).map((p) => ({ ...p }));
						const updatedPoints = currentPoints.map((p) => (p.id === id ? { ...p, checked: !p.checked } : p));
						const nextFields = updatedPoints.filter((p) => p.checked).map((p) => p.id);
						// Derive per-field color using base series color
						const baseColor = setting.color;
						const dataTypeColors = nextFields.map((fieldKey, idx) => getColorForDataType(baseColor, idx, nextFields));
						return { ...setting, fields: nextFields, dataTypeColors } as IChartSettingsType;
					});
					return updated;
				});
				// Trigger data refresh respecting existing flow
				setIsChartSettingUpdate(true);
			}
		},
		[chartType, t, applySetChartSettings, setIsChartSettingUpdate, dataPoints]
	);

	// Get data points for a specific material
	const getDataPointsForMaterial = useCallback(
		(materialKey: string) => {
			if (dataPoints[materialKey]) {
				return dataPoints[materialKey];
			}

			// Try to get chartDetails from updatedAllChartDetails first (for rearranged data)
			let chartDetails = updatedAllChartDetails[materialKey];

			// If not found in updatedAllChartDetails, try allChartDetails
			if (!chartDetails) {
				chartDetails = allChartDetails[materialKey];
			}

			// If still not found, try with index 0 (for single chart scenarios)
			if (!chartDetails) {
				const [symbol, boerse] = materialKey.split('-');
				chartDetails = allChartDetails[`${symbol}-${boerse}-0`];
			}

			const mainField = chartDetails?.originalField;
			const mainData = chartDetails?.fields || [];

			// Create default data points dynamically based on mainData
			const defaultDataPoints = mainData.map((dataPoint: string) => ({
				id: getDataTypeKey(dataPoint), // Use the key instead of full name
				label: dataPoint, // Keep the full name for display
				checked: mainField === dataPoint
			}));

			// If no mainField is found but mainData exists, default to first item being checked
			if (!mainField && mainData.length > 0) {
				defaultDataPoints[0].checked = true;
			}

			return defaultDataPoints;
		},
		[dataPoints, allChartDetails, updatedAllChartDetails]
	);

	// Utility: Get set of forecast types that have data in forCastedChartData
	const getAvailableForecastTypes = () => {
		const available = new Set<string>();
		forCastedChartData.forEach((item) => {
			const chartItem = item[`cv${symbol}-${boerse}-0`];
			if (chartItem && typeof chartItem === 'object' && 'source_data' in chartItem && chartItem.source_data) {
				const forecastData = chartItem.source_data;
				Object.keys(forecastData).forEach((key) => {
					if (forecastData[key] && Object.keys(forecastData[key]).length > 0) {
						available.add(key);
					}
				});
			}
		});
		return available;
	};

	// Utility: Get total checked data points count
	const getTotalCheckedDataPoints = useCallback(() => {
		if (!dataPoints || Object.keys(dataPoints).length === 0) {
			return 0;
		}
		// Only count selections for materials currently present in chartSettings
		const validKeys = new Set(chartSettings.map((setting, index) => `${setting.symbol}-${setting.boerse}-${index}`));
		let count = 0;
		Object.entries(dataPoints).forEach(([key, points]) => {
			if (!validKeys.has(key)) return;
			count += (points || []).filter((p) => p && p.checked).length;
		});
		return count;
	}, [dataPoints, chartSettings]);

	const availableForecastTypes = getAvailableForecastTypes();

	useEffect(() => {
		if (chartType === 'bar' && isRedirectChartScreen !== 'true') {
			setSpanObj({ span: barChartFrequencyMapper(highestPriorityFrequency) });
			if (location.state?.chartSelection?.forecastKeys && location.state?.chartSelection?.forecastKeys.length > 0) {
				setForecastKeys(location.state.chartSelection.forecastKeys);
			} else {
				setForecastKeys([]);
			}
		}
	}, [location.state]);

	useEffect(() => {
		if (hasInitializedBarDefaults.current) return; // already set once
		// Only run this initialization if the DEFAULT chart type was bar
		if (initialChartTypeRef.current !== 'bar') return;
		if (isRedirectChartScreen === 'true') return;

		const material = chartSettings[0];
		const key = `${material?.symbol}-${material?.boerse}-0`;
		if (chartSettings.length >= 2 && allChartDetails[key]?.notierung) {
			setSpanObj({ span: barChartFrequencyMapper(allChartDetails[key].notierung) });
			hasInitializedBarDefaults.current = true;
		} else if (chartSettings.length === 1 && selectedFrequencies[key]) {
			setSpanObj({ span: selectedFrequencies[key] });
			hasInitializedBarDefaults.current = true;
		}
		// setSpanObj({ span: barChartFrequencyMapper(highestPriorityFrequency) });
	}, [allChartDetails, chartSettings, isRedirectChartScreen, selectedFrequencies, setSpanObj]);

	const handleForecastToggle = (key: string) => {
		if (chartType === 'bar') {
			if (forecastKeys.length === 1 && forecastKeys[0] === key) {
				// Disable all forecasts (show only actual data)
				setForecastKeys([]);
				const updatedChartData = forCastedChartData.filter(
					(item: any) => item[`cv${symbol}-${boerse}-0`]?.type === ForeCastType.ACTUAL_PRICE
				);
				setChartData(updatedChartData);
				setPrevForecastKeys((prevKey) => {
					return [...prevKey, key];
				});
			} else {
				// Only allow enabling if this forecast type has data
				if (!availableForecastTypes.has(key)) return;
				setForecastKeys([key]);
				const otherForecasts = Array.from(availableForecastTypes).filter((forecastKey) => forecastKey !== key);
				setPrevForecastKeys(otherForecasts);
				if (forecastAccessValue === 0) {
					setIsPreviousToggleForecast(true);
				} else {
					setIsPreviousToggleForecast(false);
				}

				// Apply the same logic as chartDataBasedOnType useMemo
				const updatedChartData = forCastedChartData
					.map((item: any) => {
						const newItem = JSON.parse(JSON.stringify(item));
						const cvKey = `cv${symbol}-${boerse}-0`;
						const value = newItem[cvKey];

						if (typeof value === 'object' && value !== null) {
							const chartItem = value as IChartItem;
							// If actual data exists for this specific key, always use it
							if (chartItem.type === ForeCastType.ACTUAL_PRICE) {
								// Keep actual data as is
							} else if (chartItem.type === ForeCastType.FORECASTED_PRICE && chartItem.source_data) {
								// Only include forecast if no actual exists for this specific key/date
								// Check if actual exists for this specific key
								const actualExistsForThisKey = item[cvKey] && item[cvKey].type === ForeCastType.ACTUAL_PRICE;
								if (!actualExistsForThisKey) {
									// Only keep if forecastKeys includes this source
									const forecastSourceKeys = Object.keys(chartItem.source_data);
									if (forecastSourceKeys.length > 0 && forecastSourceKeys.includes(key)) {
										newItem[cvKey] = {
											...chartItem,
											source_data: { [key]: chartItem.source_data[key] }
										};
									} else {
										// Remove forecast if not active
										delete newItem[cvKey];
									}
								} else {
									// Remove forecast if actual exists for this key
									delete newItem[cvKey];
								}
							}
						}

						return newItem;
					})
					.filter((item) => {
						const cvKey = `cv${symbol}-${boerse}-0`;
						const data = item[cvKey];
						return data && (data.type === ForeCastType.ACTUAL_PRICE || (data.source_data && Object.keys(data.source_data).length > 0));
					});

				setChartData(updatedChartData);
			}
		} else {
			// Original logic for non-bar charts
			const latestForCastKey = [...forecastKeys];
			const forCastIndex = forecastKeys.indexOf(key as string);
			forCastIndex !== -1 ? latestForCastKey.splice(forCastIndex, 1) : latestForCastKey.push(key);

			setForecastKeys(latestForCastKey);
			setPrevForecastKeys(latestForCastKey);
			if (forecastAccessValue === 0) {
				setIsPreviousToggleForecast(true);
			} else {
				setIsPreviousToggleForecast(false);
			}

			const updatedChartData =
				latestForCastKey.length === 0
					? forCastedChartData
					: forCastedChartData
							.map((item: any) => {
								const newItem = JSON.parse(JSON.stringify(item));
								const forecastKey = newItem[`cv${symbol}-${boerse}-0`]?.source_data;

								for (let i = 0; i < latestForCastKey.length; i++) {
									if (forecastKey && latestForCastKey[i] !== undefined) {
										delete forecastKey[latestForCastKey[i]];
									}
								}
								return newItem;
							})
							.filter((item) => {
								if (isEmpty(item[`cv${symbol}-${boerse}-0`].source_data)) {
									return item[`cv${symbol}-${boerse}-0`]?.type === ForeCastType.ACTUAL_PRICE;
								}
								return item;
							});

			setChartData(updatedChartData);
		}
	};

	useEffect(() => {
		if (!location.pathname.includes('chartScreen')) {
			setIsTableOpen(false);
			let payload: { [key: string]: string } = {
				boerse: boerse,
				symbol: symbol
			};
			if (basis_symbol !== '') {
				payload = {
					...payload,
					basis_symbol: basis_symbol,
					waehrung: (defaultCurrency !== 'null' && defaultCurrency) || ''
				};
			}
			fetchFutureTableData(payload);
		}
	}, [basis_symbol, symbol, boerse, defaultCurrency, location.pathname]);

	const fetchFutureTableData = (payload: { [key: string]: string }) => {
		setFutureTableLoading(true);
		HttpService.get(API_CONFIG.path.futuretable, payload)
			.then((response) => {
				setFutureTableLoading(false);
				setFutureTableData(response);
			})
			.catch((err) => {
				setFutureTableLoading(false);
				console.error(err);
			});
	};

	const chartOrder = useCallback(
		(data: any) => {
			const convertedArray = chartSettings.map((item, settingIndex) => {
				const maxPrice = maxBy(data, (d: any) => d[`cv${item.symbol}-${item.boerse}-${settingIndex}`]?.l);
				return {
					oldKey: `cv${item.symbol}-${item.boerse}-${settingIndex}`,
					value: maxPrice,
					index: settingIndex,
					symbol: item.symbol,
					boerse: item.boerse
				};
			});
			return convertedArray;
		},
		[chartSettings]
	);

	// Guard: avoid re-posting identical payloads (prevents infinite loops)
	const lastFetchSignatureRef = useRef<string>('');
	const fetchChartData = useCallback(
		async (payload: IChartPayloadType, options?: { preserveState?: boolean }) => {
			if (!options?.preserveState) {
				setChartData([]);
				setAllChartDetails({});
			}
			setLoading(true);
			setIsFutureTable(false);

			try {
				const response = await httpService.post(API_CONFIG.path.getChartData6, { ...payload });
				// ✅ Clean data - remove bad cv... keys and their price/percentage counterparts
				const cleanedData = response.data.map((row: IChartData) => {
					const cleanedRow = { ...row };

					Object.keys(cleanedRow).forEach((key) => {
						if (key.startsWith('cv')) {
							const val = cleanedRow[key];

							const isEmptyObject = typeof val === 'object' && val !== null && Object.keys(val).length === 0;
							const allNull = typeof val === 'object' && val !== null && Object.values(val).every((v) => v === null);
							const noValidValue =
								typeof val === 'object' &&
								val !== null &&
								!['e', 'l', 't', 'o', 'h'].some((k) => {
									const item = val as IChartItem;
									const value = k === 'e' ? item.e : k === 'l' ? item.l : k === 't' ? item.t : k === 'o' ? item.o : item.h;
									return typeof value === 'number' && !isNaN(value);
								});

							if (val === null || isEmptyObject || allNull || noValidValue) {
								delete cleanedRow[key];

								const suffix = key.replace(/^cv/, '');
								delete cleanedRow[`price${suffix}`];
								delete cleanedRow[`percentage${suffix}`];
							}
						}
					});

					return cleanedRow;
				});

				const chartHighValues = chartOrder(cleanedData);
				const sortedData = [...chartHighValues].sort((a, b) => a.value - b.value);

				const newData = sortedData.map((entry, index) => ({
					...entry,
					newIndex: index,
					updatedKey: `cv${entry.symbol}-${entry.boerse}-${index}`
				}));

				cleanedData.forEach((objToUpdate: IChartData) => {
					newData.forEach(({ oldKey, updatedKey }) => {
						if (Object.prototype.hasOwnProperty.call(objToUpdate, oldKey)) {
							objToUpdate[updatedKey] = objToUpdate[oldKey];
							if (oldKey !== updatedKey) {
								delete objToUpdate[oldKey];
							}
						}
					});
				});

				const copyOfResponseHeaders = { ...response.mainHeader };
				const tempPayload: ITempObjProps[] = [];
				const tempObj: { [key: string]: IChartDetails & { chartPrice?: number } } = {};

				for (let i = 0; i < newData.length; i++) {
					const { index, symbol, boerse, newIndex } = newData[i];
					tempPayload.push(payload.data[index]);
					const headerKey = `${symbol}-${boerse}-${index}`;
					const newKey = `${symbol}-${boerse}-${newIndex}`;
					if (response.mainHeader[headerKey]) {
						tempObj[newKey] = response.mainHeader[headerKey];
					}
					// if (response.headers[headerKey]) {
					// 	tempObj[newKey] = response.headers[headerKey];
					// }
				}

				tempPayload.map((data: ITempObjProps, index: number) => {
					const key = `${data.symbol}-${data.boerse}-${index}`;
					if (tempObj[key]) {
						tempObj[key].chartPrice =
							cleanedData.length > 0 ? cleanedData[cleanedData.length - 1][`cv${data.symbol}-${data.boerse}-${index}`]?.l : 0;
					}
				});

				setRearrangedData(newData);
				setChartData(cleanedData);
				setUpdatedAllChartDetails(tempObj);
				setAllChartDetails(copyOfResponseHeaders);
				setIsIntraday(response.isIntraday);
				setLoading(false);
			} catch (error) {
				console.error('Chart Data Load Error:', error);
				setLoading(false);
			}
		},
		[chartOrder, setIsFutureTable]
	);

	const fetchSingleChartData = useCallback(
		(payload: IChartPayloadType) => {
			setChartData([]);
			setAllChartDetails({});
			setUpdatedAllChartDetails({});
			setLoading(true);

			httpService
				.post(API_CONFIG.path.getChartData6, { ...payload })
				.then((response) => {
					const copyOfResponseHeaders = { ...response.mainHeader } as { [key: string]: IChartDetails };
					setAllChartDetails(copyOfResponseHeaders);
					setUpdatedAllChartDetails(copyOfResponseHeaders);
					setChartData(response.data);
					setForecastedChartData(response.data);

					// Derive latest 'l' value from chartData for the active cv key and store into header
					try {
						const currentSymbol = chartSettings[0]?.symbol;
						const currentBoerse = chartSettings[0]?.boerse;
						const primaryKey = `${currentSymbol}-${currentBoerse}-0`;
						const cvKey = `cv${currentSymbol}-${currentBoerse}-0`;
						const dataArr = Array.isArray(response?.data) ? response.data : [];
						const lastWithL = [...dataArr]
							.reverse()
							.find((row: any) => row?.[cvKey] && typeof row[cvKey] === 'object' && row[cvKey]?.l != null);
						const lastL = lastWithL ? lastWithL[cvKey].l : 0;
						if (copyOfResponseHeaders[primaryKey]) {
							(copyOfResponseHeaders as { [key: string]: IChartDetails })[primaryKey].chartPrice = lastL;
							setAllChartDetails({ ...copyOfResponseHeaders });
							setUpdatedAllChartDetails({ ...copyOfResponseHeaders });
						}
					} catch (e) {
						// noop
					}
					const sources = copyOfResponseHeaders[`${chartSettings[0].symbol}-${chartSettings[0].boerse}-0`].sources;

					if (copyOfResponseHeaders[`${chartSettings[0].symbol}-${chartSettings[0].boerse}-0`].forecastAccess === 0) {
						if (!location.state?.chartSelection?.forecastKeys) {
							setIsToggleForecast(false);
							setForecastKeys(sources);
						} else {
							setIsToggleForecast(true);
							setForecastKeys(location.state?.chartSelection?.forecastKeys);
						}
					} else {
						if (
							chartType !== 'bar' &&
							location.state?.chartSelection?.forecastKeys &&
							location.state?.chartSelection?.forecastKeys.length > 0
						) {
							setForecastKeys(location.state.chartSelection.forecastKeys);
							setPrevForecastKeys(location.state.chartSelection.forecastKeys);
							setIsToggleForecast(true);
						} else {
							if (prevForecastKeys.length > 0 && chartType !== 'bar') {
								setForecastKeys(prevForecastKeys);
							} else {
								setIsToggleForecast(true);
							}
						}
					}
					setForecastSources(copyOfResponseHeaders[`${chartSettings[0].symbol}-${chartSettings[0].boerse}-0`].sources);
					const forecastSources = copyOfResponseHeaders[`${chartSettings[0].symbol}-${chartSettings[0].boerse}-0`].sources;
					setIsIntraday(response.isIntraday);
					setLoading(false);
					setIsChartSettingUpdate(false);

					applySetChartSettings((cs) => {
						const unit = {
							label: response.mainHeader[`${cs[0].symbol}-${cs[0].boerse}-0`]?.einheit || '',
							value: response.mainHeader[`${cs[0].symbol}-${cs[0].boerse}-0`]?.einheit || ''
						};
						const name =
							(language === 'en' &&
								response.mainHeader[`${cs[0].symbol}-${cs[0].boerse}-0`]?.name_eng !== '' &&
								response.mainHeader[`${cs[0].symbol}-${cs[0].boerse}-0`]?.name_eng) ||
							response.mainHeader[`${cs[0].symbol}-${cs[0].boerse}-0`]?.name ||
							'';
						const copyOfChartSettings = [...cs];
						copyOfChartSettings[0] = {
							...copyOfChartSettings[0],
							name: name,
							disallowed: response.mainHeader[`${cs[0].symbol}-${cs[0].boerse}-0`]?.disallowed || 0,
							unit: unit,
							symbol: response?.mainHeader[`${cs[0].symbol}-${cs[0].boerse}-0`]?.symbol,
							forecastedColor:
								!isEmpty(forecastSources) &&
								forecastSources.reduce((acc: any, key: any, idx: number) => {
									acc[key] = chartColors[idx] || chartColors[0];
									return acc;
								}, {}),
							fields: response.mainHeader[`${cs[0].symbol}-${cs[0].boerse}-0`]?.field.map((field: string) => getDataTypeKey(field)) || [
								response.mainHeader[`${cs[0].symbol}-${cs[0].boerse}-0`]?.originalField
							]
						};
						dispatch(createAction(actionTypes.UPDATE_CHART_CONFIG, false));
						return uniqBy(copyOfChartSettings, (item) => [item.symbol, item.boerse, item.id].join());
					});
				})
				.catch((error) => {
					setLoading(false);
					console.error(error, 'error');
				});
		},
		[
			chartSettings,
			chartType,
			dispatch,
			language,
			location.state?.chartSelection?.forecastKeys,
			prevForecastKeys,
			applySetChartSettings,
			setForecastSources,
			setIsChartSettingUpdate
		]
	);

	useEffect(() => {
		if (spanObj && chartType && !isEmpty(chartSettings)) {
			const { span, endDate, startDate } = spanObj;
			let payload: IChartPayloadType = {
				data: chartSettings
					.map((chart: IChartSettingsType) => {
						// If fields array exists and has items, create separate entries for each field
						if (chart.fields && chart.fields.length > 0) {
							return chart.fields.map((field: string) => {
								return {
									id: chart.id,
									symbol: chart?.symbol,
									boerse: chart?.boerse,
									waehrung: chart.currency.value,
									unit: chart.unit?.value || '',
									year: parseInt(chart.history.value) || '',
									field: getDataTypeDisplayName(field),
									currencyType: currencyType
								};
							});
						} else {
							// Default behavior when no fields are selected
							return {
								id: chart.id,
								symbol: chart?.symbol,
								boerse: chart?.boerse,
								waehrung: chart.currency.value,
								unit: chart.unit?.value || '',
								year: parseInt(chart.history.value) || '',
								currencyType: currencyType
							};
						}
					})
					.flat(), // Flatten the array since we might have nested arrays
				percentageTotal: scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL
			};
			if (span) {
				payload = {
					...payload,
					span: chartType === 'bar' ? 'max' : span
				};
			} else if (startDate && endDate) {
				payload = {
					...payload,
					start_date: startDate,
					end_date: endDate
				};
			}

			// Handle API calls based on the scenario
			// Build a stable signature to prevent duplicate requests
			const signature = JSON.stringify({
				data: payload.data,
				span: payload.span,
				start_date: payload.start_date,
				end_date: payload.end_date,
				chartType,
				scaleType,
				chartSettings,
				path: location.pathname
			});
			if (lastFetchSignatureRef.current === signature) {
				return;
			}

			if (shouldTriggerComparison && chartSettings.length >= 2) {
				// This is for comparison mode (multiple checkboxes selected)
				setBarWidth({});
				fetchChartData(payload, { preserveState: true });
				// Reset the flag after triggering the API call
				setShouldTriggerComparison(false);
			} else if (!location.pathname.includes('chartScreen') && isChartSettingUpdate && chartSettings.length === 1) {
				// This is for single chart updates (existing functionality)
				setBarWidth({});
				fetchSingleChartData(payload);
			} else if (location.pathname.includes('chartScreen') && chartSettings.length >= 2) {
				// This is for chart screen with multiple items (existing functionality)
				setBarWidth({});
				fetchChartData(payload, { preserveState: true });
			}

			// Update last signature only when we actually trigger a fetch
			lastFetchSignatureRef.current = signature;

			if (isRedirectFromGlobalSearch) {
				setChartType(chartConfig.chart_type);
			}
		}
	}, [
		chartType,
		scaleType, // recompute and fetch on chart view change
		spanObj.span,
		spanObj.startDate,
		spanObj.endDate,
		fetchSingleChartData,
		location.pathname,
		fetchChartData,
		isChartSettingUpdate,
		shouldTriggerComparison,
		chartSettings,
		chartConfig.chart_type,
		isRedirectFromGlobalSearch,
		setChartType,
		currencyType
	]);

	useEffect(() => {
		// Only proceed if we have chart details available
		if (Object.keys(allChartDetails).length === 0 && Object.keys(updatedAllChartDetails).length === 0) {
			return;
		}

		const selectedDataTypes = location?.state?.chartSelection?.selectedDataTypesPerMaterial || {};

		const newDataPoints: {
			[materialKey: string]: Array<{ id: string; label: string; checked: boolean }>;
		} = {};

		chartSettings.forEach((setting, index) => {
			const materialKey = `${setting.symbol}-${setting.boerse}-${index}`;

			// If we already have selections for this material, preserve them
			if (dataPoints[materialKey] && dataPoints[materialKey].length > 0) {
				return; // do not override existing selections
			}

			// Prefer updatedAllChartDetails first
			let chartDetails = updatedAllChartDetails[materialKey];

			// If not found in updatedAllChartDetails, try allChartDetails
			if (!chartDetails) {
				chartDetails = allChartDetails[materialKey];
			}

			// If still not found, try with index 0 (for single chart scenarios)
			if (!chartDetails && index === 0) {
				chartDetails = allChartDetails[`${setting.symbol}-${setting.boerse}-0`];
			}

			const mainField = chartDetails?.originalField;
			const mainData = chartDetails?.fields || [];

			// Build data points
			let defaultDataPoints = mainData.map((dataPoint: string) => ({
				id: getDataTypeKey(dataPoint),
				label: dataPoint,
				checked: mainField === dataPoint
			}));

			// If `selectedDataTypesPerMaterial` exists for this materialKey → use that to set checked state
			if (selectedDataTypes[materialKey]?.length) {
				defaultDataPoints = mainData.map((dataPoint: string) => ({
					id: getDataTypeKey(dataPoint),
					label: dataPoint,
					checked: selectedDataTypes[materialKey].includes(getDataTypeKey(dataPoint))
				}));
			} else if (!mainField && mainData.length > 0) {
				// Fallback to first item being checked
				defaultDataPoints[0].checked = true;
			}

			newDataPoints[materialKey] = defaultDataPoints;
		});

		// Avoid triggering a rerender if nothing new to add
		if (Object.keys(newDataPoints).length === 0) return;

		setDataPoints((prev) => ({
			...prev,
			...newDataPoints
		}));

		// Initialize fields in chartSettings based on checked data points
		// Only update chartSettings if there is an actual change required to avoid render loops
		let shouldUpdateFields = false;
		const planner: { index: number; nextFields: string[] }[] = [];
		chartSettings.forEach((setting, index) => {
			const materialKey = `${setting.symbol}-${setting.boerse}-${index}`;
			const dataPointsForMaterial = newDataPoints[materialKey] || dataPoints[materialKey] || [];
			const checkedFields = dataPointsForMaterial.filter((point) => point.checked).map((point) => point.id);
			if (checkedFields.length > 0) {
				const prevFields = Array.isArray(setting.fields) ? setting.fields : [];
				const sameLength = prevFields.length === checkedFields.length;
				const sameValues = sameLength && every(checkedFields, (f, i) => f === prevFields[i]);
				if (!sameValues) {
					planner.push({ index, nextFields: checkedFields });
					shouldUpdateFields = true;
				}
			}
		});

		if (shouldUpdateFields) {
			applySetChartSettings((prevSettings) => {
				return prevSettings.map((setting, index) => {
					const plan = planner.find((p) => p.index === index);
					if (plan) {
						return { ...setting, fields: plan.nextFields };
					}
					return setting;
				});
			});
		}
	}, [
		chartSettings,
		allChartDetails,
		updatedAllChartDetails,
		location?.state?.chartSelection?.selectedDataTypesPerMaterial,
		applySetChartSettings
	]);

	const handleChartType = useCallback(
		(selectedOption: string) => {
			// Enhanced validation for bar chart switching
			if (selectedOption === 'bar') {
				// Check if switching to bar chart would exceed the 3-material limit
				if (chartSettings.length > 3) {
					notify(t(localizationConstants.barChartNumberValidationMessage), 'error');
					return;
				}

				// Check if current data point selection would exceed the 3-data-point limit
				const totalCheckedDataPoints = getTotalCheckedDataPoints();

				if (totalCheckedDataPoints > 3) {
					notify(t(localizationConstants.barChartNumberValidationMessage), 'error');
					return;
				}
			} else if (scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL) {
				notify(t(localizationConstants.barChartErrorMessage), 'error');
				return;
			}

			if (selectedOption === 'bar') {
				// Save current forecastKeys for restoration later

				setSpanObj({ span: barChartFrequencyMapper(highestPriorityFrequency) });
				setPrevForecastKeys(forecastKeys);
				const material = chartSettings[0];
				const key = `${material?.symbol}-${material?.boerse}-0`;
				if (chartSettings.length >= 2) {
					setSpanObj({ span: barChartFrequencyMapper(allChartDetails[key].notierung) });
				} else if (chartSettings.length === 1) {
					setSpanObj({ span: selectedFrequencies[key] });
				}
				// setSpanObj({span: })
				setChartData([]);
				// Set forecastKeys to bar logic (e.g., only one or empty)
				setForecastKeys([]); // or set to the correct bar default
				const availableSources = allChartDetails[chartDetailKey].sources;

				// Find forecast source that has data and should be rendered
				const forecastSourceThatWillRender =
					!isEmpty(availableSources) &&
					availableSources.find((src) => {
						// check if any forecast data exists for this source
						return forCastedChartData.some((item: any) => {
							const cvKey: any = Object.keys(item).find((k) => k.startsWith('cv'));
							const forecastData = item?.[cvKey]?.source_data?.[src];
							const forecastType = item?.[cvKey]?.type;

							const actualExists = item?.[cvKey]?.type === ForeCastType.ACTUAL_PRICE;

							// Forecast data exists but not when actual also exists for that period
							return forecastData && forecastType === ForeCastType.FORECASTED_PRICE && !actualExists;
						});
					});

				if (Array.isArray(prevForecastKeys) && prevForecastKeys.includes(forecastSourceThatWillRender as string)) {
					setPrevForecastKeys(availableSources);
				}
				// Do NOT reset forecastKeys here
				// Show only actual data initially
				if (location.state?.chartSelection?.forecastKeys) {
					const { color } = location.state.chartSelection;
					const newState = {
						...location.state,
						chartSelection: {
							...(color ? { color } : {})
						}
					};
					navigate(location.pathname + location.search, { state: newState, replace: true });
				}
				const updatedChartData = forCastedChartData.filter(
					(item: any) => item[`cv${symbol}-${boerse}-0`]?.type === ForeCastType.ACTUAL_PRICE
				);
				setChartData(updatedChartData);
			} else if (selectedOption === 'candle') {
				setSpanObj({ span: '6m' });
			} else {
				setSpanObj({ span: '1y' });
				if (location.state?.chartSelection?.forecastKeys) {
					const newState = {
						...location.state,
						chartSelection: {
							forecastKeys: undefined
						}
					};
					navigate(location.pathname + location.search, { state: newState, replace: true });
				}
				if (chartType === 'bar' && Array.isArray(prevForecastKeys) && prevForecastKeys.length === 0) {
					setForecastKeys([]);
				}
			}
			setChartType(selectedOption);
			setIsChartSettingUpdate(true);
		},
		[
			forCastedChartData,
			symbol,
			boerse,
			chartSettings,
			highestPriorityFrequency,
			scaleType,
			location.state,
			location.pathname,
			location.search,
			navigate,
			forecastKeys,
			prevForecastKeys,
			chartType,
			allChartDetails,
			chartDetailKey,
			dataPoints,
			getTotalCheckedDataPoints,
			selectedFrequencies,
			setIsChartSettingUpdate
		]
	);

	const getComparativeChartData = useCallback(
		(comparativeData: ISearchMaterialData) => {
			// Enhanced validation for bar chart comparative data
			if (chartType === 'bar') {
				// Check if adding another material would exceed the 3-material limit
				if (chartSettings.length > 2) {
					notify(t(localizationConstants.barChartNumberValidationMessage), 'error');
					return;
				}

				// Check if current data point selection would exceed the 3-data-point limit
				// Only check if dataPoints exist and have been initialized
				if (dataPoints && Object.keys(dataPoints).length > 0) {
					const totalCheckedDataPoints = getTotalCheckedDataPoints();
					// If already 3 fields selected in bar mode, block adding another chart
					if (totalCheckedDataPoints >= 3) {
						notify(t(localizationConstants.barChartNumberValidationMessage), 'error');
						return;
					}
				}
			} else {
				// For non-bar charts, cap total selected data points at 9
				if (dataPoints && Object.keys(dataPoints).length > 0) {
					const totalCheckedDataPoints = getTotalCheckedDataPoints();
					if (totalCheckedDataPoints >= 9) {
						notify(t(localizationConstants.errorMessageForChartTypeSelection), 'error');
						return;
					}
				}
			}

			// Allow material comparison if within limits
			if (chartSettings.length < 9) {
				const copyOfChartSettings = [
					...chartSettings.map((item: IChartSettingsType) => {
						return {
							...item,
							axis: {
								label:
									scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL ? 'Left' : 'Right',
								value: scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL ? 'left' : 'right'
							}
						};
					})
				];
				const isCompareWithSelfEnabled = copyOfChartSettings.some((chart) => chart.history.value !== '');

				// Get used colors
				const usedColors = new Set(copyOfChartSettings.map((item) => item.color));

				// Find first available color
				let newColor = colorPallet[0];
				for (let i = 0; i < colorPallet.length; i++) {
					if (!usedColors.has(colorPallet[i])) {
						newColor = colorPallet[i];
						break;
					}
				}

				copyOfChartSettings.push({
					id: uuid(),
					name:
						language === 'en' && comparativeData.name_eng !== ''
							? comparativeData.name_eng
							: comparativeData.name_deu !== ''
							? comparativeData.name_deu
							: comparativeData.name,
					symbol: comparativeData.symbol,
					boerse: comparativeData.boerse,
					disallowed: comparativeData.disallowed || 0,
					unit: { label: comparativeData.einheit, value: comparativeData.einheit },
					currency: { label: comparativeData.waehrung, value: comparativeData.waehrung },
					indicators: [],
					history: {
						label: `${isCompareWithSelfEnabled ? new Date().getFullYear() - 1 : t(localizationConstants.none)}`,
						value: `${isCompareWithSelfEnabled ? new Date().getFullYear() - 1 : ''}`
					},
					axis: {
						label: scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL ? 'Left' : 'Right',
						value: scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL ? 'left' : 'right'
					},
					color: newColor,
					basis_symbol: comparativeData.basis_symbol
				});
				// Clear any previous data type selections for the soon-to-be-added material index
				const newIndex = copyOfChartSettings.length - 1;
				const materialKeyToReset = `${comparativeData.symbol}-${comparativeData.boerse}-${newIndex}`;
				setDataPoints((prev) => {
					const next = { ...prev } as { [materialKey: string]: Array<{ id: string; label: string; checked: boolean }> };
					delete next[materialKeyToReset];
					return next;
				});
				const { span, endDate, startDate } = spanObj;
				const locationSpan = chartType === 'bar' ? barChartFrequencyMapper(highestPriorityFrequency) : span;
				const state = {
					advanceChartConfig: {
						chartSettings: isCompareWithSelfEnabled
							? copyOfChartSettings
							: uniqBy(copyOfChartSettings, (item) => [item.symbol, item.boerse, item.id].join())
					} as IAdvanceChartConfigType,
					chartSelection: {
						type: scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL ? 'line' : chartType,
						span: locationSpan,
						customChartSpan: {
							von: startDate,
							bis: endDate
						},
						chartView:
							scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL
								? scaleType
								: ScaleType.PRICE_AXIS
					},
					name: location?.state?.name || ''
				};
				if (elementId) {
					navigate(`/materials/chartScreen?timeSpan=${timeSpan}&currency=${currency}${elementId && `&element_id=${elementId}`}`, {
						state: state
					});
				} else {
					navigate(`/materials/chartScreen`, {
						state: state
					});
				}
			} else if (chartSettings.length > 8) {
				notify(t(localizationConstants.errorMessageForChartTypeSelection), 'error');
			}
		},
		[
			chartSettings,
			chartType,
			scaleType,
			currency,
			elementId,
			highestPriorityFrequency,
			language,
			location?.state?.name,
			navigate,
			spanObj,
			t,
			timeSpan,
			dataPoints,
			getTotalCheckedDataPoints
		]
	);

	const handleSpan = (value: string) => {
		setBarWidth({});
		setSpanObj({ span: value });
		setIsChartSettingUpdate(true);
		if (chartType === 'bar') {
			if (chartSettings.length === 1) {
				const material = chartSettings[0];
				const materialKey = `${material?.symbol}-${material?.boerse}-0`;
				setSelectedFrequencies((prev) => ({ ...prev, [materialKey]: value }));
			} else if (chartSettings.length >= 2) {
				setSelectedFrequencies((prev) => {
					const next = { ...prev };
					chartSettings.forEach((item, index) => {
						const key = `${item?.symbol}-${item?.boerse}-${index}`;
						next[key] = value as string;
					});
					return next;
				});
			}
		}
	};

	const handleOnRowClick = useCallback(
		(data: any) => {
			const newKey = `${data.symbol}-${data.boerse}-0`;
			setActiveChartKey(newKey);
			dispatch(createAction(actionTypes.UPDATE_CHART_CONFIG, false));

			// Get used colors
			const usedColors = new Set(chartSettings.map((item) => item.color));

			// Find first available color
			let newColor = colorPallet[0];
			for (let i = 0; i < colorPallet.length; i++) {
				if (!usedColors.has(colorPallet[i])) {
					newColor = colorPallet[i];
					break;
				}
			}

			const newChartSettings = [
				{
					symbol: data.symbol,
					boerse: data.boerse,
					basis_symbol: basis_symbol,
					currency: {
						label: defaultCurrency || '',
						value: defaultCurrency || ''
					},
					axis: {
						label: scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL ? 'Left' : 'Right',
						value: scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL ? 'left' : 'right'
					},
					color: newColor,
					disallowed: 0,
					history: {
						label: t(localizationConstants.none),
						value: ''
					},
					id: uuid(),
					indicators: [],
					name: '',
					unit: {
						label: '',
						value: ''
					}
				}
			];

			// Clear any previous data type selections for this single-chart context (index 0)
			setDataPoints((prev) => {
				const next = { ...prev } as { [materialKey: string]: Array<{ id: string; label: string; checked: boolean }> };
				delete next[`${data.symbol}-${data.boerse}-0`];
				return next;
			});

			// First set all the necessary states
			applySetChartSettings(newChartSettings);
			setMaterialSymbol(data.symbol);
			setIsFutureTable(true);
			setIsChartSettingUpdate(true);
		},
		[basis_symbol, scaleType, defaultCurrency, dispatch, applySetChartSettings, setIsChartSettingUpdate, setIsFutureTable, setMaterialSymbol, t]
	);

	const onMouseEnter = () => {
		const pageWrapper = document.getElementById('scrollable-wrapper');
		if (pageWrapper) {
			pageWrapper.style.overflow = 'hidden';
		}
	};

	const onMouseLeave = () => {
		const pageWrapper = document.getElementById('scrollable-wrapper');
		if (pageWrapper) {
			pageWrapper.style.overflow = 'auto';
		}
	};

	useEffect(() => {
		const tempArray = [];
		if (!isEmpty(rearrangedData)) {
			for (let i = 0; i < rearrangedData.length; i++) {
				const { index } = rearrangedData[i];
				if (chartSettings[index]) {
					tempArray.push(chartSettings[index]);
				}
			}
		}
		setUpdatedChartSettings(!location.pathname.includes('chartScreen') ? chartSettings : tempArray);
	}, [chartSettings, rearrangedData]);

	const handleAdvanceChartConfiguration = (values: FormikValues) => {
		const data = { ...values };
		setUpdateChartViewLoading(true);

		setAction('');

		// Build a non-mutating next allChartDetails map to avoid re-render loops
		const keys = data.chartSettings.map((item: any, index: number) => `${item.symbol}-${item.boerse}-${index}`);
		const nextAllChartDetails: typeof allChartDetails = {} as any;
		Object.keys(allChartDetails).forEach((key: string) => {
			if (keys.includes(key)) {
				(nextAllChartDetails as any)[key] = (allChartDetails as any)[key];
			}
		});
		setAllChartDetails({ ...nextAllChartDetails });
		setUpdateChartViewLoading(false);

		// --- Chart Type Logic Fix ---
		let nextChartType = chartType;
		let nextSpan = spanObj.span;

		// If user explicitly selects bar, always respect it
		if (data.chartSettings.length > 0 && data.chartSettings[0].chartType === 'bar') {
			nextChartType = 'bar';
		} else if (data.scaleType === ScaleType.PERCENTAGE_CHANGE || data.scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL) {
			nextChartType = 'line';
			// When switching to percentage change (line chart), set span to '1y' if current span is 'max' (bar chart span)
			if (spanObj.span === '1d') {
				nextSpan = '1y';
			}
		} else {
			nextChartType = chartType;
		}

		if (data.chartSettings.length > 1) {
			navigate(`/materials/chartScreen?timeSpan=${timeSpan || nextSpan}${elementId && `&element_id=${elementId}`}`, {
				state: {
					advanceChartConfig: data,
					chartSelection: {
						type:
							data.scaleType === ScaleType.PERCENTAGE_CHANGE || data.scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL
								? 'line'
								: nextChartType,
						span: nextSpan,
						chartView: data.scaleType,
						currencyType: data.currencyType,
						forecastKeys: forecastKeys
					}
				}
			});
		} else if (chartSettings.length > 1 && data.chartSettings.length === 1) {
			const { boerse, symbol, basis_symbol } = data.chartSettings[0];
			const { endDate, startDate } = spanObj;
			const navigationPath = `/materials/futureTable?boerse=${replaceHashtagFromUrl(boerse)}&basis_symbol=${
				basis_symbol !== null ? replaceHashtagFromUrl(basis_symbol) : ''
			}&symbol=${replaceHashtagFromUrl(symbol)}&currency=${defaultCurrency || ''}&timeSpan=${timeSpan || nextSpan}`;
			navigate(navigationPath, {
				state: location.pathname.includes('chartScreen')
					? {
							advanceChartConfig: data,
							chartSelection: {
								currency: data.chartSettings[0].currency.value,
								indicator: data.chartSettings[0]?.indicators || [],
								span: nextSpan,
								type:
									data.scaleType === ScaleType.PERCENTAGE_CHANGE || data.scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL
										? 'line'
										: nextChartType,
								chartColor: data.chartSettings[0]?.color || chartColor,
								unit: data.chartSettings[0]?.unit.value || '',
								history: data.chartSettings[0]?.history.value || '',
								axis: data.chartSettings[0]?.axis.value || 'right',
								customChartSpan: {
									von: startDate,
									bis: endDate
								},
								chartView: data.scaleType,
								currencyType: data.currencyType,
								forecastKeys: forecastKeys
							}
					  }
					: {}
			});
			setIsChartSettingUpdate(true);
		} else if (chartSettings.length === 1 && data.chartSettings.length === 1) {
			const { boerse, symbol, basis_symbol } = data.chartSettings[0];
			const { endDate, startDate } = spanObj;

			if (location.pathname.includes('materials/futureTable')) {
				const navigationPath = `/materials/futureTable?boerse=${replaceHashtagFromUrl(boerse)}&basis_symbol=${
					basis_symbol ? replaceHashtagFromUrl(basis_symbol) : ''
				}&symbol=${replaceHashtagFromUrl(symbol)}&currency=${currency}&timeSpan=${timeSpan || nextSpan}${
					elementId && `&element_id=${elementId}`
				}`;
				navigate(navigationPath, {
					state: {
						advanceChartConfig: data,
						chartSelection: {
							currency: data.chartSettings[0].currency.value,
							indicator: data?.chartSettings[0].indicators || [],
							span: nextSpan,
							type:
								data.scaleType === ScaleType.PERCENTAGE_CHANGE || data.scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL
									? 'line'
									: nextChartType,
							chartColor: data?.chartSettings[0].color || chartColor,
							unit: data?.chartSettings[0].unit.value || '',
							history: data?.chartSettings[0].history.value || '',
							axis: data?.chartSettings[0].axis.value || 'right',
							customChartSpan: {
								von: startDate,
								bis: endDate
							},
							chartView: data.scaleType,
							currencyType: data.currencyType,
							forecastKeys: forecastKeys
						}
					}
				});
			}
			applySetChartSettings(data.chartSettings);
			setChartType(nextChartType);
			// Update spanObj if span changed
			if (nextSpan !== spanObj.span) {
				setSpanObj({ span: nextSpan });
			}
			setIsChartSettingUpdate(true);
		}
		setScaleType(data.scaleType);
		setCurrencyType(data.currencyType);
		setChartColor(data.chartSettings[0].color);
	};

	const { isLeftAxis, isRightAxis } = useMemo(
		() => ({
			isLeftAxis: filter(chartSettings, { axis: { value: ChartAxisPosition.LEFT_AXIS } }).length > 0,
			isRightAxis: filter(chartSettings, { axis: { value: ChartAxisPosition.RIGHT_AXIS } }).length > 0
		}),
		[chartSettings]
	);

	useEffect(() => {
		if (!isEmpty(forCastedChartData)) {
			const chartDataKey = chartSettings.map((item, index) => `cv${item.symbol}-${item.boerse}-${index}`);
			const chartType: any = !isEmpty(forCastedChartData) && forCastedChartData[forCastedChartData.length - 1][chartDataKey[0]];
			if (chartType !== undefined && chartType.type === ForeCastType.FORECASTED_PRICE) {
				setIsVisibleForecastNotation(true);
				setIsVisiblePdfModuleNotation(true);
			} else {
				setIsVisiblePdfModuleNotation(false);
				if (chartType !== undefined && chartType.type !== ForeCastType.FORECASTED_PRICE) {
					setIsVisibleForecastNotation(false);
				}
			}
		} else {
			const isUpdatedEmpty = isEmpty(Object.values(updatedAllChartDetails).filter((item: any) => item.disallowed === 0));

			if (isUpdatedEmpty) {
				setIsVisibleForecastNotation(true);
			}
		}
	}, [forCastedChartData, chartSettings, isVisibleForecastNotation, isVisiblePdfModuleNotation, updatedAllChartDetails]);

	const colorList =
		!isEmpty(allChartDetails) && allChartDetails[`${symbol}-${boerse}-0`] && allChartDetails[`${symbol}-${boerse}-0`]?.sources !== undefined
			? allChartDetails[`${symbol}-${boerse}-0`].sources.reduce((acc: any, key: any, index: number) => {
					acc[key] = chartColors[index];
					return acc;
			  }, {})
			: {};

	useEffect(() => {
		// ✅ If chartSettings are passed via location.state (like from global search), update it
		if (location.state?.advanceChartConfig?.chartSettings) {
			const newChartSettings = location.state.advanceChartConfig.chartSettings;

			applySetChartSettings(newChartSettings);

			const key = `${newChartSettings[0].symbol}-${newChartSettings[0].boerse}-0`;
			setActiveChartKey(key); // Make sure this matches your active key logic
			setIsChartSettingUpdate(true); // ✅ This will trigger fetch in the existing useEffect
		} else {
			setActiveChartKey('');
		}
	}, [location.state, applySetChartSettings, setIsChartSettingUpdate]);

	const handleChartFrequency = useCallback(() => {
		const data = chartSettings.map((item, index) => allChartDetails[`${item?.symbol}-${item?.boerse}-${index}`]?.notierung);
		setChartFrequency(data);
	}, [allChartDetails, chartSettings]);

	useEffect(() => {
		if (!isEmpty(allChartDetails) && !isEmpty(chartSettings)) {
			handleChartFrequency();
		}
	}, [allChartDetails, chartSettings, handleChartFrequency]);

	useEffect(() => {
		if (
			chartType === 'bar' &&
			forCastedChartData.length > 0 &&
			allChartDetails[chartDetailKey]?.sources?.length > 0 &&
			!location.pathname.includes('/dashboard') &&
			chartSettings.length === 1
		) {
			const availableSources = allChartDetails[chartDetailKey].sources;

			// Find forecast source that has data and should be rendered
			const forecastSourceThatWillRender = availableSources.find((src) => {
				// check if any forecast data exists for this source
				return forCastedChartData.some((item: any) => {
					const cvKey: any = Object.keys(item).find((k) => k.startsWith('cv'));
					const forecastData = item?.[cvKey]?.source_data?.[src];
					const forecastType = item?.[cvKey]?.type;

					const actualExists = item?.[cvKey]?.type === ForeCastType.ACTUAL_PRICE;

					// Forecast data exists but not when actual also exists for that period
					return forecastData && forecastType === ForeCastType.FORECASTED_PRICE && !actualExists;
				});
			});

			if (forecastSourceThatWillRender) {
				let keysToUse: string[] = [];
				if (location?.state?.chartSelection?.forecastKeys) {
					// setForecastKeys(location.state.chartSelection.forecastKeys);
					keysToUse = location.state.chartSelection.forecastKeys;
					const otherForecasts = availableSources.filter(
						(forecastKey) => !location.state.chartSelection.forecastKeys.includes(forecastKey)
					);
					setPrevForecastKeys(otherForecasts);
				} else {
					if (!prevForecastKeys.includes(forecastSourceThatWillRender) && forecastAccessValue !== 0) {
						// setForecastKeys([forecastSourceThatWillRender]);
						keysToUse = [forecastSourceThatWillRender];
						const otherForecasts = availableSources.filter((forecastKey) => forecastKey !== forecastSourceThatWillRender);
						setPrevForecastKeys(otherForecasts);
					} else {
						keysToUse = [];

						if (isPreviousToggleForecast && !prevForecastKeys.includes(forecastSourceThatWillRender)) {
							setForecastKeys([forecastSourceThatWillRender]);
							keysToUse = [forecastSourceThatWillRender];
							const otherForecasts = availableSources.filter((forecastKey) => forecastKey !== forecastSourceThatWillRender);
							setPrevForecastKeys(otherForecasts);
						} else {
							// if (location?.state?.chartSelection?.prevForecastKeys && !prevForecastKeys.includes(forecastSourceThatWillRender)) {
							// 	setPrevForecastKeys([]);
							// }
							setForecastKeys([]);
							keysToUse = [];
						}
					}
				}

				setForecastKeys(keysToUse);
				const updatedChartData = forCastedChartData
					.map((item) => {
						const newItem = JSON.parse(JSON.stringify(item));
						const cvKey: any = Object.keys(newItem).find((k) => k.startsWith('cv'));
						const forecastKey = newItem[cvKey]?.source_data;

						if (forecastKey) {
							Object.keys(forecastKey).forEach((k) => {
								if (!keysToUse.includes(k)) {
									delete forecastKey[k];
								}
							});
						}

						return newItem;
					})
					.filter((item) => {
						const cvKey: any = Object.keys(item).find((k) => k.startsWith('cv'));
						const data = item[cvKey]?.source_data;
						return item[cvKey]?.type === ForeCastType.ACTUAL_PRICE || (data && Object.keys(data).length > 0);
					});

				setChartData(updatedChartData);
			} else {
				// Only actual data
				setForecastKeys([]);
				const updatedChartData = forCastedChartData.filter((item: any) => {
					const cvKey: any = Object.keys(item).find((k) => k.startsWith('cv'));
					return item[cvKey]?.type === ForeCastType.ACTUAL_PRICE;
				});
				setChartData(updatedChartData);
			}
		}
	}, [chartType, forCastedChartData, allChartDetails, symbol, boerse, chartDetailKey]);

	// Monitor data point changes and validate bar chart limits
	useEffect(() => {
		if (chartType === 'bar') {
			const totalCheckedDataPoints = getTotalCheckedDataPoints();

			// If more than 3 data points are selected in bar chart mode, show warning
			if (totalCheckedDataPoints > 3) {
				notify(t(localizationConstants.barChartNumberValidationMessage), 'error');
			}
		}
	}, [dataPoints, chartType, getTotalCheckedDataPoints, t]);

	useEffect(() => {
		if (location.search.includes('prevForecastKey')) {
			setPrevForecastKeys([]);
		}
	}, [location.search, setPrevForecastKeys]);

	const filteredChartData = useMemo(() => {
		if (!forecastKeys || forecastKeys.length === 0) {
			if (isToggleForecast) {
				return chartData;
			} else {
				const updatedChartData = chartData.filter((item: any) => {
					const cvKey: any = Object.keys(item).find((k) => k.startsWith('cv'));
					return item[cvKey]?.type === ForeCastType.ACTUAL_PRICE;
				});
				return updatedChartData;
			}
		}

		if (chartType !== 'bar') {
			return chartData
				.map((dataPoint) => {
					const newDataPoint = { ...dataPoint };
					Object.keys(newDataPoint).forEach((key) => {
						const value = newDataPoint[key];
						if (
							key.startsWith('cv') &&
							value &&
							typeof value === 'object' &&
							!Array.isArray(value) &&
							'source_data' in value &&
							value.source_data &&
							typeof value.source_data === 'object'
						) {
							const newSourceData = { ...value.source_data };
							(forecastKeys || []).forEach((symbol: string) => {
								delete newSourceData[symbol];
							});
							if (Object.keys(newSourceData).length === 0) {
								delete newDataPoint[key];
							} else {
								newDataPoint[key] = {
									...value,
									source_data: newSourceData
								};
							}
						}
					});
					return newDataPoint;
				})
				.filter((dataPoint) => {
					return Object.keys(dataPoint).some((key) => key.startsWith('cv'));
				});
		} else {
			// For bar chart: Only keep actual and selected forecast key(s)
			return chartData
				.map((dataPoint) => {
					const newDataPoint = { ...dataPoint };
					Object.keys(newDataPoint).forEach((key) => {
						const value = newDataPoint[key];
						if (
							key.startsWith('cv') &&
							value &&
							typeof value === 'object' &&
							!Array.isArray(value) &&
							'source_data' in value &&
							value.source_data &&
							typeof value.source_data === 'object'
						) {
							const allowedKeys = ['actual', ...(forecastKeys || [])];
							const filteredSourceData: any = {};
							allowedKeys.forEach((k) => {
								if (value.source_data[k] !== undefined) {
									filteredSourceData[k] = value.source_data[k];
								}
							});
							if (Object.keys(filteredSourceData).length === 0) {
								delete newDataPoint[key];
							} else {
								newDataPoint[key] = {
									...value,
									source_data: filteredSourceData
								};
							}
						}
					});
					return newDataPoint;
				})
				.filter((dataPoint) => {
					return Object.keys(dataPoint).some((key) => key.startsWith('cv'));
				});
		}
	}, [chartData, chartType, forecastKeys]);

	//Get all forecast sources found in chartData
	const availableForecastsInChartData = useMemo(() => {
		const forecasts = new Set<string>();

		chartData?.forEach((dataPoint) => {
			Object.values(dataPoint).forEach((val) => {
				if (typeof val === 'object' && val !== null && val.type === ForeCastType.FORECASTED_PRICE && val.source_data) {
					Object.keys(val.source_data).forEach((sourceKey) => {
						forecasts.add(sourceKey);
					});
				}
			});
		});

		return forecasts;
	}, [chartData]);

	// Filter visible sources (remove past forecasts)
	const visibleForecastSources = useMemo(() => {
		const allSources = allChartDetails[chartDetailKey]?.sources || [];
		return allSources.filter((item) => {
			return (
				availableForecastsInChartData.has(item) || forecastKeys.includes(item) || (chartType === 'bar' && availableForecastTypes.has(item))
			);
		});
	}, [allChartDetails, chartDetailKey, availableForecastsInChartData, forecastKeys, chartType, availableForecastTypes]);

	// Add this before the return statement
	const selectedDataTypesPerMaterial = useMemo(() => {
		const result: { [materialKey: string]: string[] } = {};
		updatedChartSettings.forEach((item, index) => {
			const key = `${item.symbol}-${item.boerse}-${index}`;
			result[key] = (getDataPointsForMaterial(key) || []).filter((point) => point.checked).map((point) => point.id);
		});
		return result;
	}, [updatedChartSettings, getDataPointsForMaterial]);

	// Initialize selectedFrequencies from material notierung when details load or chartSettings change
	useEffect(() => {
		if (isEmpty(allChartDetails) || isEmpty(chartSettings)) return;

		// ✅ Merge behavior:
		// - Start with existing selections (prev)
		// - Apply any navigation-provided selectedFrequencies (takes precedence for those keys)
		// - For any material keys missing a frequency, fill with default mapped from notierung
		const stateSelected = location?.state?.chartSelection?.selectedFrequencies as Record<string, string> | undefined;

		setSelectedFrequencies((prev) => {
			let merged: Record<string, string> = { ...prev };
			if (stateSelected && !isEmpty(stateSelected)) {
				merged = { ...merged, ...stateSelected };
			}
			chartSettings.forEach((item, index) => {
				const key = `${item?.symbol}-${item?.boerse}-${index}`;
				if (!merged[key]) {
					const notierung = allChartDetails[key]?.notierung as string | null;
					const materialDefault = notierung ? barChartFrequencyMapper(notierung) : undefined;
					// Strict: only set when notierung provides a default; otherwise keep existing
					if (materialDefault) merged[key] = materialDefault as string;
				}
			});

			const prevStr = JSON.stringify(prev);
			const mergedStr = JSON.stringify(merged);
			if (prevStr === mergedStr) return prev; // no change
			return merged;
		});

		// ✅ Mark as initialized to avoid resetting after user change
	}, [allChartDetails, chartSettings, location?.state]);

	return (
		<>
			<div className='futuretable-main'>
				<div className={(isTableOpen && 'd-flex') || ''}>
					{!location.pathname.includes('chartScreen') && !location.search.includes('isIframe') && (
						<MaterialTables
							chartData={chartData}
							chartDetails={allChartDetails[`${chartSettings[0]?.symbol || symbol}-${chartSettings[0]?.boerse || boerse}-0`]}
							currency={currency as string}
							futureTableData={futureTableData}
							handleOnRowClick={handleOnRowClick}
							loading={futureTableLoading}
							materialTableLoading={loading}
							setIsTableOpen={setIsTableOpen}
							isTableOpen={isTableOpen}
							setIsChartSettingUpdate={() => setIsChartSettingUpdate(true)}
							dateMatchKey={`cv${chartSettings[0]?.symbol}-${chartSettings[0]?.boerse}-0`}
						/>
					)}
					<div className={isTableOpen && !location.pathname.includes('chartScreen') ? `width--70 chart_screen` : 'full-width'}>
						{chartSettings.map((header, index) => {
							const { symbol, boerse, id, indicators, currency } = header;
							const chartKey =
								chartSettings.length === 1 ? activeChartKey || `${symbol}-${boerse}-${index}` : `${symbol}-${boerse}-${index}`;

							return (
								<ChartHeader
									key={`${symbol}-${boerse}-${id}`}
									color={chartSettings[index]?.color || chartColor}
									chartDetails={updatedAllChartDetails[chartKey] || allChartDetails[chartKey] || {}}
									indicators={indicators}
									headerIndex={index}
									chartSettings={chartSettings}
									scaleType={scaleType}
									setChartSettings={setChartSettings}
									chartColor={chartColor}
									chartType={chartType}
									currency={currency.value}
									spanObj={spanObj}
									setChartData={setChartData}
									setAllChartDetails={setAllChartDetails}
									chartId={id}
									setIsChartSettingUpdate={() => setIsChartSettingUpdate(true)}
									dataPoints={getDataPointsForMaterial(`${symbol}-${boerse}-${index}`)}
									handleCheckboxChange={(id) => handleCheckboxChange(`${symbol}-${boerse}-${index}`, id)}
									selectedFrequencies={selectedFrequencies}
									setSelectedFrequencies={setSelectedFrequencies}
									isTableOpen={isTableOpen}
								/>
							);
						})}
						<div
							className={`chart ${chartSettings.length === 1 && chartSettings[0]?.indicators?.length > 0 ? 'mt-16' : 'mt-40'} ${
								!location.search.includes('isIframe') ? 'mt-40' : 'mt-15 chart-iframe'
							} bg-color--white`}
						>
							{loading && (
								<div className='d-flex justify-content-space-between align-items-center chart-indicators mb-10'>
									<div className='d-flex align-items-center gap--10 indicator-section-1'>
										<Skeleton height={24} width={24} />
										<div className='d-flex align-items-center gap--10'>
											<Skeleton height={24} width={200} />
											<Skeleton height={24} width={200} />
											<Skeleton height={24} width={200} />
										</div>
									</div>
								</div>
							)}
							{!isEmpty(allChartDetails) &&
								!loading &&
								!isEmpty(Object.values(allChartDetails).filter((item) => item.disallowed === 0)) && (
									<ChartConfigurationDropdowns
										chartType={chartType}
										handleChartType={handleChartType}
										getComparativeChartData={getComparativeChartData}
										handleSpan={handleSpan}
										spanObj={spanObj}
										setAction={setAction}
										disabledSearch={loading || priceListLoading}
										chartDetails={
											allChartDetails[`${updatedChartSettings[0]?.symbol}-${updatedChartSettings[0]?.boerse}-0`] || {}
										}
										setIsChartSettingUpdate={() => setIsChartSettingUpdate(true)}
										setSpanObj={setSpanObj}
									/>
								)}
							<div
								className={`chart-wrapper ${isLeftAxis && isRightAxis ? 'left-right-axis' : isRightAxis ? 'right-axis' : ''}`}
								id={`chart-container-main-${updatedChartSettings[0]?.symbol}-0`}
								onMouseEnter={onMouseEnter}
								onMouseLeave={onMouseLeave}
							>
								{loading && <Loader />}
								{!loading &&
									!isEmpty(updatedAllChartDetails) &&
									Object.values(updatedAllChartDetails).some((item) => item.disallowed === 1) && (
										<div className='d-flex justify-content-center align-items-center locked-chart' style={{ height: '550px' }} />
									)}
								{isEmpty(chartData) &&
									!loading &&
									!isEmpty(updatedAllChartDetails) &&
									every(Object.values(updatedAllChartDetails), ['disallowed', 0]) && <EmptyDataContainer />}
								{!isEmpty(chartData) &&
									!isEmpty(updatedChartSettings) &&
									updatedAllChartDetails[`${updatedChartSettings[0].symbol}-${updatedChartSettings[0].boerse}-0`] &&
									!loading &&
									!isEmpty(Object.values(updatedAllChartDetails).filter((item) => item.disallowed === 0)) && (
										<StockChart
											chartData={filteredChartData}
											chartType={chartType}
											showZoomButton={true}
											loading={loading}
											chartDetails={
												updatedAllChartDetails[`${updatedChartSettings[0]?.symbol}-${updatedChartSettings[0]?.boerse}-0`]
											}
											cardIndex={0}
											chartDesignConfig={{
												margin: {
													left: defineChartMargins(updatedChartSettings, false)?.left as number,
													right: defineChartMargins(updatedChartSettings, false)?.right as number,
													top: 10,
													bottom: 40
												},
												fontSize: 12,
												rectWidth: 55,
												rectHeight: 20
											}}
											spanObj={spanObj}
											setChartColor={setChartColor}
											chartName='main'
											chartSettings={updatedChartSettings}
											scaleType={scaleType}
											isIntraday={isIntraday}
											allChartDetails={updatedAllChartDetails}
											barWidth={barWidth}
											setBarWidth={setBarWidth}
											forecastKeys={forecastKeys}
											selectedDataTypesPerMaterial={selectedDataTypesPerMaterial}
											selectedFrequencies={selectedFrequencies}
											setIsExportDisabled={setIsExportDisabled}
										/>
									)}
							</div>
						</div>
					</div>
				</div>

				<div className='d-flex justify-content-end mt-10'>
					<div className='d-flex gap--10 chart-forecast-indicator__mapper'>
						<label className='form-control-forecast font--13px font-weight--500'>
							<div className='circle' style={{ border: `2px solid ${chartSettings[0]?.color || 'white'}` }}>
								<div className='inner-circle' style={{ background: `${chartSettings[0]?.color || 'white'}` }} />
							</div>
							<Translate text={localizationConstants.actualData} />
						</label>
						{isVisibleForecastNotation &&
							chartSettings.length < 2 &&
							visibleForecastSources?.map((item, index) => {
								const isActiveForecast = forecastKeys.includes(item);
								const isBarChart = chartType === 'bar';
								const cleanedItemName = cleanForecastItemName(item);

								const isBarChartToggleSwitch = isActiveForecast && isToggleForecast;
								const isToggleSwitch = !forecastKeys?.includes(item) && isToggleForecast;
								return (
									<label
										key={index}
										className='form-control-forecast font--13px font-weight--500'
										style={{
											backgroundColor: colorList !== undefined && colorList[item] ? hexToRgba(colorList[item], 0.2) : undefined,
											borderRadius: '16px 5px 5px 16px',
											paddingRight: '8px'
										}}
									>
										<ToggleSwitch
											color={colorList !== undefined && colorList[item]}
											onChange={() => {
												handleForecastToggle(item);
												setIsToggleForecast(true);
											}}
											value={isBarChart ? isBarChartToggleSwitch : isToggleSwitch}
											disabled={forecastAccessValue === 0}
										/>
										{forecastAccessValue === 0 && (
											<div className='circle mr-0' data-tooltip-id='forecast-tooltip'>
												<div className='unlock-icon'>
													<ForeCastUnlockIcon width='15' height='15' />
												</div>
											</div>
										)}
										<span
											style={{
												textTransform: 'capitalize',
												marginRight: '2px',
												marginLeft: `${forecastAccessValue === 0 ? '-2px' : '8px'}`
											}}
										>
											{`${cleanedItemName} `}{' '}
										</span>
									</label>
								);
							})}

						{forecastAccessValue === 0 && (
							<Tooltip
								place='top'
								className='no-padding forecast-chart-info--tooltip'
								content={t(localizationConstants.lockForecastText) || ''}
								clickable
								id='forecast-tooltip'
							/>
						)}
					</div>
				</div>
				{!location.pathname.includes('chartScreen') &&
					!location.search.includes('isIframe') &&
					!isEmpty(allChartDetails[`${chartSettings[0]?.symbol}-${chartSettings[0]?.boerse}-0`]) && (
						<ChartAdditionalInfo
							chartData={chartData}
							futureTableData={futureTableData}
							chartDetails={allChartDetails[`${chartSettings[0]?.symbol}-${chartSettings[0]?.boerse}-0`]}
							spanObj={spanObj}
							updateAllChartDetails={updatedAllChartDetails}
						/>
					)}
				{!location.search.includes('isIframe') && (
					<div className='add-widget'>
						{chartSettings.length > 0 &&
							!isEmpty(allChartDetails) &&
							!isEmpty(allChartDetails[`${chartSettings[0]?.symbol}-${chartSettings[0]?.boerse}-0`]) && (
								<ExportMaterialDataButtons
									chartData={chartData}
									chartDetails={allChartDetails[`${chartSettings[0]?.symbol}-${chartSettings[0]?.boerse}-0`]}
									chartSelection={{
										currency: chartSettings[0].currency.value,
										indicator: chartSettings[0]?.indicators || [],
										span: spanObj.span,
										type: chartType,
										chartColor: chartSettings[0]?.color || chartColor,
										unit: chartSettings[0]?.unit.value || '',
										history: chartSettings[0]?.history.value || '',
										axis: chartSettings[0]?.axis.value || 'right',
										chartView: scaleType,
										currencyType: currencyType,
										forecastKeys: forecastKeys,
										selectedDataTypesPerMaterial: selectedDataTypesPerMaterial,
										selectedFrequencies: selectedFrequencies
									}}
									id={`chart-container-main-${updatedChartSettings[0]?.symbol}-0`}
									action={action}
									setAction={setAction}
									symbol={symbol}
									comparativeChartIndicator={chartSettings.slice(1)}
									customChartSpan={{
										bis: spanObj.endDate || '',
										von: spanObj.startDate || ''
									}}
									strokeColors={strokeColors}
									chartSettings={chartSettings}
									scaleType={scaleType}
									compareValue={compareValue}
									forecastKeys={forecastKeys}
									chartFrequency={chartFrequency}
									updateAllChartDetails={updatedAllChartDetails}
									isExportDisabled={isExportDisabled}
								/>
							)}
					</div>
				)}
				{chartSettings.length === 1 &&
					!location.search.includes('isIframe') &&
					!isEmpty(allChartDetails) &&
					!isEmpty(allChartDetails[`${chartSettings[0]?.symbol}-${chartSettings[0]?.boerse}-0`]) && (
						<PermissionManager permission={adminPermissionMapping.superDataUserAdmin}>
							<AdminExportButtons
								chartDetails={allChartDetails[`${chartSettings[0]?.symbol}-${chartSettings[0]?.boerse}-0`]}
								setPriceListLoading={setPriceListLoading}
								isIntraday={isIntraday}
							/>
						</PermissionManager>
					)}
			</div>
			{action === 'chartSettings' && (
				<ChartSettingsPopup
					chartSettings={chartSettings}
					isVisible={action === 'chartSettings'}
					setAction={setAction}
					scaleType={scaleType}
					handleOnSubmit={handleAdvanceChartConfiguration}
					updateChartViewLoading={updateChartViewLoading}
					chartType={chartType}
					chartDetails={allChartDetails}
					currencyType={currencyType}
				/>
			)}
		</>
	);
};

export default ChartScreenContainer;
